import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, switchMap} from 'rxjs/operators';
import {from, of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {
	ApproveShiftRequest,
	ApproveShiftRequestSuccess,
	ApproveShifts,
	ApproveShiftsSuccess,
	DeleteShifts,
	DeleteShiftsSuccess,
	EActionsActions,
	GetShiftRequests,
	GetShiftRequestsSuccess,
	GetShiftsPendingApproval,
	GetShiftsPendingApprovalSuccess, GetShiftSwapRequests, GetShiftSwapRequestsSuccess,
	RemoveShiftRequest,
	RemoveShiftRequestSuccess,
} from '@app/core/store/actions/actions.actions';
import {ShiftService} from '@app/modules/shared/services/shift.service';
import {EQOLogLevel, QOLogService} from '@quickorder/angular-web-lib';
import {TranslateService} from '@ngx-translate/core';
import {ActionService} from '@app/core/services/api/actions/action.service';

@Injectable()
export class ActionsEffects {

	@Effect()
	public onGetShiftsPendingApproval$ = this.actions$.pipe(
		ofType<GetShiftsPendingApproval>(EActionsActions.GetShiftsPendingApproval),
		switchMap((action: GetShiftsPendingApproval) => from(this.shiftService.pendingApprovalShifts(action.filter, action.page_size, action.page)).pipe(
			switchMap(res => [
				new GetShiftsPendingApprovalSuccess(res.data, res.pagination),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onApproveShifts$ = this.actions$.pipe(
		ofType<ApproveShifts>(EActionsActions.ApproveShifts),
		switchMap((action: ApproveShifts) => from(this.shiftService.massShiftApprove(action.payload)).pipe(
			switchMap(res => {
				const skippedShiftsById = res.skipped_shifts.map(s => s.id);
				if (skippedShiftsById.length > 0) {
					this.logService.logContent({
						level: EQOLogLevel.ERROR,
						title: this.translate.instant('ACTIONS.PENDING_APPROVAL.ONE_OR_MORE_OVERLAPS'),
						actions: [],
					});
				}

				return [
					new ApproveShiftsSuccess(action.payload.filter(s => skippedShiftsById.indexOf(s.id) === -1).map(s => s.id)),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onDeleteShifts$ = this.actions$.pipe(
		ofType<DeleteShifts>(EActionsActions.DeleteShifts),
		switchMap((action: DeleteShifts) => from(this.shiftService.massShiftDelete(action.payload)).pipe(
			switchMap((res: any) => [
				new DeleteShiftsSuccess(action.payload.map(s => s.id)),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	// New design

	@Effect()
	public onGetShiftRequests$ = this.actions$.pipe(
		ofType<GetShiftRequests>(EActionsActions.GetShiftRequests),
		switchMap((action: GetShiftRequests) => from(this.actionsService.getShiftRequests(action.page, action.page_size)).pipe(
			switchMap((res: any) => [
				new GetShiftRequestsSuccess(res.data, res.pagination),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetShiftSwapRequests$ = this.actions$.pipe(
		ofType<GetShiftSwapRequests>(EActionsActions.GetShiftSwapRequests),
		switchMap((action: GetShiftSwapRequests) => from(this.actionsService.getShiftSwapRequests(action.page, action.page_size)).pipe(
			switchMap((res: any) => [
				new GetShiftSwapRequestsSuccess(res.data, res.pagination),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onApproveShiftRequests$ = this.actions$.pipe(
		ofType<ApproveShiftRequest>(EActionsActions.ApproveShiftRequest),
		switchMap((action: ApproveShiftRequest) => from(this.actionsService.approveShiftRequest(action.shift.id, action.userId)).pipe(
			switchMap(() => [
				new ApproveShiftRequestSuccess(action.shift),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onRemoveShiftRequests$ = this.actions$.pipe(
		ofType<RemoveShiftRequest>(EActionsActions.RemoveShiftRequest),
		switchMap((action: RemoveShiftRequest) => from(this.actionsService.removeShiftRequest(action.shift.id)).pipe(
			switchMap((res: any) => [
				new RemoveShiftRequestSuccess(action.shift),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	constructor(
		private actions$: Actions,
		private shiftService: ShiftService,
		private logService: QOLogService,
		private translate: TranslateService,
		private actionsService: ActionService,
	) {
	}
}
